import { Input, Spinner, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEnrollmentProjection } from '../../hooks/useEnrollmentProjection';
import { ShowErrors } from '../Messages';
import styles from './EnrollmentProjection.module.scss';

const cx = classNames.bind(styles);

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const EnrollmentProjection = () => {
  const { t } = useTranslation();
  const { enrollmentProjection, isLoading, error, selectedDate, setSelectedDate } = useEnrollmentProjection();

  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();
  const debouncedSetDate = useCallback(
    (date: string) => {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => setSelectedDate(date), 500);
    },
    [setSelectedDate]
  );

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const parsed = dayjs(value);
    const year = value.split('-')[0];
    if (parsed.isValid() && year[0] !== '0') {
      debouncedSetDate(parsed.format('YYYY-MM-DD'));
    }
  };

  if (error) {
    return <ShowErrors content={error.message} />;
  }

  if (isLoading) {
    return (
      <div className={cx('spinnerContainer')}>
        <Spinner size={WidgetSizeEnum.MEDIUM} />
      </div>
    );
  }

  return (
    <div className={cx('container')}>
      <div className={cx('table')}>
        <div className={cx('tableHeader')}>
          <h2 className="text-xl">{t('enrollmentProjection.header.title')}</h2>
          <div className={cx('datePickerContainer')}>
            <Input
              name="enrollmentDate"
              inputType="date"
              value={selectedDate}
              onChange={handleDateChange}
              label={t('enrollmentProjection.column.date')}
              wrapperClasses="flex flex-row items-center gap-2"
            />
          </div>
        </div>
        <div className={cx('tableContent')}>
          {/* Header Row */}
          <div className={cx('row', 'headerRow')}>
            <div className={cx('cell', 'labelCell')}></div>
            {WEEKDAYS.map((day, index) => (
              <div key={index} className={cx('cell')}>
                <span className={cx('fullDayName')}>{t(day)}</span>
                <span className={cx('shortDayName')}>{t(`enrollmentProjection.column.${day.charAt(0)}`)}</span>
              </div>
            ))}
            <div className={cx('cell')}>
              <span className={cx('fullDayName', 'scheduleNotRecorded')}>
                <span>{t('enrollmentProjection.column.scheduleNotRecorded.line1')}</span>
                <span>{t('enrollmentProjection.column.scheduleNotRecorded.line2')}</span>
              </span>
              <span className={cx('shortDayName')}>{t('enrollmentProjection.column.scheduleNotRecordedShort')}</span>
            </div>
          </div>
          {/* Under 2 Row */}
          <div className={cx('row')}>
            <div className={cx('cell', 'labelCell')}>{t('enrollmentProjection.row.under2')}</div>
            {enrollmentProjection.under2.map((count, index) => (
              <div key={index} className={cx('cell')}>
                {count}
              </div>
            ))}
          </div>
          {/* Over 2 Row */}
          <div className={cx('row')}>
            <div className={cx('cell', 'labelCell')}>{t('enrollmentProjection.row.over2')}</div>
            {enrollmentProjection.over2.map((count, index) => (
              <div key={index} className={cx('cell')}>
                {count}
              </div>
            ))}
          </div>
          {/* DOB Unknown Row */}
          <div className={cx('row')}>
            <div className={cx('cell', 'labelCell')}>
              <span className={cx('fullText')}>{t('enrollmentProjection.row.dateOfBirth')}</span>
              <span className={cx('shortText')}>{t('enrollmentProjection.row.dateOfBirthShort')}</span>
            </div>
            {enrollmentProjection.dobUnknown.map((count, index) => (
              <div key={index} className={cx('cell')}>
                {count}
              </div>
            ))}
          </div>
          {/* Total Row */}
          <div className={cx('row', 'totalRow')}>
            <div className={cx('cell', 'labelCell')}>{t('enrollmentProjection.row.total')}</div>
            {enrollmentProjection.total.map((count, index) => (
              <div key={index} className={cx('cell')}>
                {count}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrollmentProjection;
